import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button, Card, FormField, Input } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';

export function SignupPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    tenantName: '',
    tenantSlug: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onChange = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);
    try {
      const r = await api().post('/auth/signup', form);
      useAuthStore.getState().setSession({
        accessToken: r.data.tokens.accessToken,
        expiresIn: r.data.tokens.expiresIn,
        user: r.data.user,
        tenant: r.data.tenant,
      });
      navigate('/dashboard');
    } catch (e) {
      const resp = (e as { response?: { data?: { code?: string; errors?: Array<{ field: string; code: string; message?: string }> } } }).response?.data;
      if (resp?.code === 'validation.failed' && resp.errors?.length) {
        const map: Record<string, string> = {};
        for (const err of resp.errors) {
          // Prefer a localized message based on the zod code; fall back to the API's English message.
          const localized = tc(`errors:${err.code}`, { defaultValue: '' });
          map[err.field] = localized || err.message || err.code;
        }
        setFieldErrors(map);
      } else if (resp?.code === 'conflict') {
        setError(t('signup.errors.conflict', { defaultValue: tc('errors:conflict') }));
      } else {
        setError(tc(`errors:${resp?.code ?? 'server'}`, { defaultValue: tc('errors:server') }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card size="lg">
      <div className="mb-7">
        <h1 className="text-2xl font-semibold tracking-tight">{t('signup.title')}</h1>
        <p className="mt-1.5 text-sm text-fg-muted">{t('signup.subtitle')}</p>
      </div>

      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-5">
          <FormField id="fullName" label={t('signup.fullName')} required error={fieldErrors.fullName}>
            <Input id="fullName" value={form.fullName} onChange={onChange('fullName')} invalid={!!fieldErrors.fullName} required />
          </FormField>
          <FormField id="email" label={t('signup.email')} required error={fieldErrors.email}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={onChange('email')}
              invalid={!!fieldErrors.email}
              placeholder="you@company.com"
              required
            />
          </FormField>
          <FormField
            id="password"
            label={t('signup.password')}
            helperText={!fieldErrors.password ? t('signup.passwordHint') : undefined}
            error={fieldErrors.password}
            required
          >
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={form.password}
                onChange={onChange('password')}
                invalid={!!fieldErrors.password}
                className="pe-16"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute inset-y-0 end-2 my-auto h-7 rounded px-2 text-xs font-medium text-fg-muted outline-none transition hover:text-fg focus-visible:ring-2 focus-visible:ring-brand/30"
                aria-pressed={showPassword}
                aria-label={showPassword
                  ? t('login.hidePassword', { defaultValue: 'Hide password' })
                  : t('login.showPassword', { defaultValue: 'Show password' })}
              >
                {showPassword
                  ? t('login.hide', { defaultValue: 'Hide' })
                  : t('login.show', { defaultValue: 'Show' })}
              </button>
            </div>
          </FormField>
        </div>

        <div className="flex items-center gap-3 py-1 text-xs uppercase tracking-wide text-fg-muted">
          <div className="h-px flex-1 bg-border" />
          <span>{t('signup.workspaceSection', { defaultValue: 'Your workspace' })}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="flex flex-col gap-5">
          <FormField id="tenantName" label={t('signup.tenantName')} required error={fieldErrors.tenantName}>
            <Input id="tenantName" value={form.tenantName} onChange={onChange('tenantName')} invalid={!!fieldErrors.tenantName} required />
          </FormField>
          <FormField
            id="tenantSlug"
            label={t('signup.tenantSlug')}
            helperText={!fieldErrors.tenantSlug ? t('signup.tenantSlugHint') : undefined}
            error={fieldErrors.tenantSlug}
            required
          >
            <Input
              id="tenantSlug"
              value={form.tenantSlug}
              onChange={onChange('tenantSlug')}
              invalid={!!fieldErrors.tenantSlug}
              placeholder="acme-co"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
            />
          </FormField>
        </div>

        {error && (
          <div
            className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger"
            role="alert"
          >
            {error}
          </div>
        )}

        <Button type="submit" size="lg" loading={loading} className="w-full">
          {t('signup.submit')}
        </Button>
      </form>

      <div className="my-7 h-px bg-border" />

      <p className="text-center text-sm text-fg-muted">
        {t('signup.haveAccount')}{' '}
        <Link
          to="/login"
          className="font-medium text-brand underline-offset-4 hover:underline focus:outline-none focus-visible:underline"
        >
          {t('signup.signInLink')}
        </Link>
      </p>
    </Card>
  );
}
